import { useState, useEffect } from "react";
import { onMessage } from "firebase/messaging";
import { messaging, generateToken } from "../notifications/firebase";
import axiosInstance from "../utils/axiosInstance";
import {
  Box,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Text,
  Flex,
  VStack,
  Image,
  useColorModeValue,
} from "@chakra-ui/react";
import { FaBell, FaTimes, FaMobileAlt } from "react-icons/fa";

const NotificationComponent = ({ userId }) => {
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [isPwaInstalled, setIsPwaInstalled] = useState(false);
  const [isInstallPromptAvailable, setIsInstallPromptAvailable] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isPwaModalOpen, setIsPwaModalOpen] = useState(false);

  const bgColor = useColorModeValue("gray.900", "gray.900");
  const textColor = useColorModeValue("white", "white");
  const buttonGradient = "linear(to-r, #FF0080, #7928CA)";

  const backendUrl =
    process.env.NODE_ENV === "production"
      ? process.env.REACT_APP_BACKEND_URL
      : "http://localhost:8080";

  const sendTokenToBackend = async (token) => {
    try {
      await axiosInstance.post(`${backendUrl}/update-fcm-token`, {
        fcm_token: token,
        user_id: userId,
      });
      console.log("FCM Token successfully sent to backend.");
    } catch (error) {
      console.error("Error sending FCM token to backend:", error);
    }
  };

  useEffect(() => {
    console.log("🖥 Checking PWA & Notification Status...");
  
    const existingToken = localStorage.getItem("fcm_token");
  
    // ✅ Detect if the app is installed as a PWA
    const isStandalone = () => {
      const isIosStandalone = window.navigator.standalone;
      const isWebStandalone = window.matchMedia("(display-mode: standalone)").matches;
      return isIosStandalone || isWebStandalone;
    };
  
    const pwaInstalled = isStandalone();
    setIsPwaInstalled(pwaInstalled);
  
    // 🔥 LOGIC FLOW
    if (pwaInstalled) {
      if (!existingToken) {
        // ✅ If PWA is installed but no FCM token → Show Notifications Modal
        console.log("🔔 PWA installed, but no notification token found. Showing Notifications Modal...");
        setIsNotificationOpen(true);
      }
    } else {
      // ✅ If PWA is NOT installed
      if (!existingToken) {
        // Check if browser supports `beforeinstallprompt` event (used for PWA install)
        if (window.matchMedia("(display-mode: browser)").matches) {
          console.log("📲 App is NOT installed yet, prompting PWA installation...");
          setIsPwaModalOpen(true);
        } else {
          console.log("📲 PWA Install Not Supported in This Mode.");
        }
      }
    }
  
    // ✅ Listen for the PWA install prompt event (works for Chrome Desktop, Android, etc.)
    const handleBeforeInstallPrompt = (e) => {
      console.log("📲 beforeinstallprompt event fired");
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallPromptAvailable(true);
    };
  
    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
  
    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    };
  }, []);
  
  
  const handleAllowNotifications = async () => {
    console.log("🔔 Checking Notification Support...");
  
    // ✅ If browser does NOT support notifications, open PWA prompt instead
    if (!("Notification" in window) || !messaging) {
      console.warn("⚠️ Notifications not supported on this browser.");
      setIsNotificationOpen(false);
  
      // ✅ Force Open PWA Modal if it's installable
      if (!isPwaInstalled && deferredPrompt) {
        console.log("📲 Opening PWA Install Modal since notifications are unavailable...");
        setIsPwaModalOpen(true);
      }
      return;
    }
  
    // ✅ Try requesting FCM token
    const token = await generateToken();
    if (token) {
      console.log("✅ Notifications enabled! FCM Token:", token);
      setPermissionGranted(true);
      localStorage.setItem("fcm_token", token);
      await sendTokenToBackend(token);
  
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker
          .register("/firebase-messaging-sw.js")
          .then((registration) => console.log("✅ Service Worker registered:", registration))
          .catch((err) => console.error("❌ Service Worker registration failed:", err));
      }
  
      onMessage(messaging, (payload) => {
        console.log("📩 Message received:", payload);
      });
  
      setIsNotificationOpen(false);
  
      // ✅ Open PWA Modal if NOT installed
      if (!isPwaInstalled && deferredPrompt) {
        console.log("📲 Opening PWA Install Modal after notifications enabled...");
        setIsPwaModalOpen(true);
      }
    } else {
      console.error("❌ FCM Token generation failed.");
  
      // ✅ If token fails, still allow PWA prompt
      setIsNotificationOpen(false);
      if (!isPwaInstalled && deferredPrompt) {
        console.log("📲 Notifications failed, opening PWA modal...");
        setIsPwaModalOpen(true);
      }
    }
  };
  
  const handleInstallPWA = async () => {
    if (deferredPrompt) {
      console.log("📲 Showing Install Prompt...");
      deferredPrompt.prompt();
      const choiceResult = await deferredPrompt.userChoice;
      console.log("User choice:", choiceResult);
      
      if (choiceResult.outcome === "accepted") {
        console.log("✅ User accepted PWA install");
      } else {
        console.log("❌ User dismissed PWA install");
      }
  
      setDeferredPrompt(null); // Reset the prompt
      setIsPwaModalOpen(false); // Close modal
    }
  };
  
  
  

  const handleDenyNotifications = () => {
    console.log("❌ Notifications denied.");
    setIsNotificationOpen(false);
  
    // ✅ If notifications are denied, still check for PWA
    if (!isPwaInstalled && deferredPrompt) {
      console.log("📲 User denied notifications, opening PWA modal...");
      setIsPwaModalOpen(true);
    }
  };
  
  

  return (
    <Box>
      {/* Notification Modal */}
      {!permissionGranted && isNotificationOpen && (
        <Modal isOpen={isNotificationOpen} onClose={handleDenyNotifications} motionPreset="slideInBottom">
          <ModalOverlay />
          <ModalContent bg={bgColor} borderRadius="xl" boxShadow="xl" p={4}>
            <ModalHeader textAlign="center" fontSize="lg" fontWeight="bold" color={textColor}>
              <Flex align="center" justify="center">
                <FaBell size="24px" style={{ marginRight: "8px" }} />
                Enable Real-Time Notifications
              </Flex>
            </ModalHeader>
            <ModalCloseButton />

            <ModalBody>
              <Text textAlign="center" fontSize="md" color={textColor}>
                Stay updated with real-time notifications by allowing browser notifications.
              </Text>
            </ModalBody>

            <ModalFooter justifyContent="center">
              <Button
                onClick={handleAllowNotifications}
                bgGradient="linear(to-r, #6B46C1, #9F7AEA)"
                color="white"
                _hover={{ bg: "#553C9A", bgGradient: "linear(to-r, #553C9A, #6B46C1)" }}
                mr={3}
              >
                Allow
              </Button>
              <Button
                variant="outline"
                color={textColor}
                borderColor={textColor}
                _hover={{ bg: "purple.900", color: "white" }}
                onClick={handleDenyNotifications}
              >
                Deny
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {/* PWA Install Modal */}
      {(isInstallPromptAvailable || !isPwaInstalled) && isPwaModalOpen && (
        <Modal isOpen={isPwaModalOpen} onClose={() => setIsPwaModalOpen(false)} motionPreset="slideInBottom">
          <ModalOverlay />
          <ModalContent bg={bgColor} borderRadius="xl" boxShadow="xl" p={4}>
            <ModalHeader textAlign="center" fontSize="lg" fontWeight="bold" color={textColor}>
              <Flex align="center" justify="center">
                <FaMobileAlt size="24px" style={{ marginRight: "8px" }} />
                Add PapiRazzi to Your Home Screen
              </Flex>
            </ModalHeader>
            <ModalCloseButton />

            <ModalBody>
              <VStack spacing={4} align="center">
                <Text fontSize="md" textAlign="center" color={textColor}>
                  Open the app like a native experience by adding it to your home screen.
                </Text>
                <Image
                  src="/AddPWAInstructions.gif"
                  alt="Add PWA Instructions"
                  borderRadius="md"
                  maxW="100%"
                />
                <Text fontSize="md" fontWeight="bold" color={textColor}>
                  Steps to Add:
                </Text>
                <Box textAlign="center">
                  <Text color={textColor}>📤 Tap <strong>Share</strong> in your browser menu.</Text>
                  <Text color={textColor}>📲 Swipe up and select <strong>Add to Home Screen</strong>.</Text>
                  <Text color={textColor}>✅ Confirm by tapping <strong>Add</strong>.</Text>
                </Box>
              </VStack>
            </ModalBody>

            <ModalFooter justifyContent="center">
            <Button
              onClick={handleInstallPWA}
              bgGradient="linear(to-r, #6B46C1, #9F7AEA)"
              color="white"
              _hover={{ bg: "#553C9A", bgGradient: "linear(to-r, #553C9A, #6B46C1)" }}
              mr={3}
            >
                Install Now
              </Button>
              <Button
                variant="outline"
                color={textColor}
                borderColor={textColor}
                _hover={{ bg: "purple.900", color: "white" }}
                onClick={() => setIsPwaModalOpen(false)}
              >
                Later
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

    </Box>
  );
};

export default NotificationComponent;
